import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, API_URL, downloadText, exportEntries, getCorrectionStats, getCostCodes, getCustomers, getEntries, getEntryCount, getUsers } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getLastNDaysRange, groupEntriesByDay, toISODate } from "@/lib/time";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

export type AdminViewModelValue = ReturnType<typeof useAdminViewModelValue>;

const AdminViewModelContext = React.createContext<AdminViewModelValue | null>(null);

function useAdminViewModelValue() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const roles = useDashboardRoles();
  const { canViewReports, canEditPunches, managedEditUserIds, isAdmin } = roles;

  // Filter searchable states for admin report
  const [filterCostCodeSearch, setFilterCostCodeSearch] = React.useState("");
  const [filterCostCodeOpen, setFilterCostCodeOpen] = React.useState(false);
  const filterCostCodeRef = React.useRef<HTMLDivElement>(null);
  const [filterCustomerSearch, setFilterCustomerSearch] = React.useState("");
  const [filterCustomerOpen, setFilterCustomerOpen] = React.useState(false);
  const filterCustomerRef = React.useRef<HTMLDivElement>(null);
  const [filterEmployeeSearch, setFilterEmployeeSearch] = React.useState("");
  const [filterEmployeeOpen, setFilterEmployeeOpen] = React.useState(false);
  const filterEmployeeRef = React.useRef<HTMLDivElement>(null);
  const [filterCostCode, setFilterCostCode] = React.useState("all");
  const [filterCustomer, setFilterCustomer] = React.useState("all");
  const [filterEmployee, setFilterEmployee] = React.useState("all");
  const [fromDate, setFromDate] = React.useState(() => {
    const { from } = getLastNDaysRange(7);
    return from;
  });
  const [toDate, setToDate] = React.useState(() => {
    const { to } = getLastNDaysRange(7);
    return to;
  });
  const [exportError, setExportError] = React.useState<string | null>(null);
  const [entriesPage, setEntriesPage] = React.useState(0);
  const [entriesPageSize, setEntriesPageSize] = React.useState(25);
  const [entriesSearch, setEntriesSearch] = React.useState("");
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);
  const [payWeekDefaultApplied, setPayWeekDefaultApplied] = React.useState(false);
  const { data: payWeeksData } = useQuery({
    queryKey: ["pay-weeks", currentYear],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/pay-weeks?year=${currentYear}`, { credentials: "include" });
      if (!resp.ok) return { pay_weeks: [] as { pay_week: string; from_date: string; to_date: string; label: string }[], current: null as { pay_week: string; from_date: string; to_date: string; label: string } | null };
      return resp.json() as Promise<{ pay_weeks: { pay_week: string; from_date: string; to_date: string; label: string }[]; current: { pay_week: string; from_date: string; to_date: string; label: string } | null }>;
    },
    enabled: !!user,
  });
  React.useEffect(() => {
    if (payWeekDefaultApplied || !payWeeksData?.current) return;
    const c = payWeeksData.current;
    setFromDate(c.from_date);
    setToDate(c.to_date);
    setPayWeekDefaultApplied(true);
  }, [payWeeksData, payWeekDefaultApplied]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterCustomerRef.current && !filterCustomerRef.current.contains(event.target as Node)) {
        setFilterCustomerOpen(false);
      }
      if (filterCostCodeRef.current && !filterCostCodeRef.current.contains(event.target as Node)) {
        setFilterCostCodeOpen(false);
      }
      if (filterEmployeeRef.current && !filterEmployeeRef.current.contains(event.target as Node)) {
        setFilterEmployeeOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: filterCostCodesData } = useQuery({
    queryKey: ["cost-codes-filter", filterCostCodeSearch],
    queryFn: () => getCostCodes(filterCostCodeSearch),
    enabled: !!user,
  });
  const { data: filterCustomersData } = useQuery({
    queryKey: ["customers-filter", filterCustomerSearch],
    queryFn: () => getCustomers(filterCustomerSearch),
    enabled: !!user,
  });

  const { data: filterEmployeesData } = useQuery({
    queryKey: ["users-filter", filterEmployeeSearch],
    queryFn: () => getUsers({ include_inactive: true, search: filterEmployeeSearch.trim() || undefined, limit: 100, offset: 0 }),
    enabled: !!user && canViewReports,
  });



  const entryParams = React.useMemo(() => {
    const params = new URLSearchParams();
    if (filterCostCode !== "all") params.set("cost_code_id", filterCostCode);
    if (filterCustomer !== "all") params.set("customer_id", filterCustomer);
    if (filterEmployee !== "all") params.set("user_id", filterEmployee);
    if (fromDate) params.set("from_date", `${fromDate}T00:00:00`);
    if (toDate) params.set("to_date", `${toDate}T23:59:59`);
    if (entriesSearch.trim()) params.set("search", entriesSearch.trim());
    params.set("limit", String(entriesPageSize));
    params.set("offset", String(entriesPage * entriesPageSize));
    return params;
  }, [filterCostCode, filterCustomer, filterEmployee, fromDate, toDate, entriesSearch, entriesPage, entriesPageSize]);

  const {
    data: entriesData,
    isLoading: entriesLoading,
  } = useQuery({
    queryKey: ["entries", entryParams.toString()],
    queryFn: () => getEntries(entryParams),
    enabled: !!user,
  });

  const { data: entriesCountData } = useQuery({
    queryKey: ["entries-count", entryParams.toString().replace(/&offset=\d+&limit=\d+/, "")],
    queryFn: () => {
      const countParams = new URLSearchParams(entryParams);
      countParams.delete("offset");
      countParams.delete("limit");
      return getEntryCount(countParams);
    },
    enabled: !!user,
  });

  const {
    data: correctionStats,
    isLoading: correctionStatsLoading,
  } = useQuery({
    queryKey: ["correction-stats", fromDate, toDate],
    queryFn: () =>
      getCorrectionStats({
        from_date: fromDate ? `${fromDate}T00:00:00` : undefined,
        to_date: toDate ? `${toDate}T23:59:59` : undefined,
      }),
    enabled: !!user && canViewReports,
  });
  const filteredEntries = entriesData ?? [];
  const chartData = React.useMemo(() => groupEntriesByDay(entriesData ?? []).slice(-14), [entriesData]);

  async function handleExport() {
    setExportError(null);
    try {
      const exportParams = new URLSearchParams(entryParams);
      exportParams.delete("offset");
      exportParams.delete("limit");
      const csv = await exportEntries(exportParams);
      downloadText(`timesheets_${toISODate(new Date())}.csv`, csv, "text/csv;charset=utf-8");
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 429) {
          setExportError("Rate limited. Please wait a minute before trying to export again.");
        } else {
          setExportError(error.message || "Export failed.");
        }
      } else {
        setExportError("Export failed.");
      }
    }
  }


  return {
    ...roles,
    user,
    queryClient,
    filterCostCode,
    setFilterCostCode,
    filterCustomer,
    setFilterCustomer,
    filterEmployee,
    setFilterEmployee,
    filterCostCodeSearch,
    setFilterCostCodeSearch,
    filterCostCodeOpen,
    setFilterCostCodeOpen,
    filterCostCodeRef,
    filterCustomerSearch,
    setFilterCustomerSearch,
    filterCustomerOpen,
    setFilterCustomerOpen,
    filterCustomerRef,
    filterEmployeeSearch,
    setFilterEmployeeSearch,
    filterEmployeeOpen,
    setFilterEmployeeOpen,
    filterEmployeeRef,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    entriesSearch,
    setEntriesSearch,
    entriesPage,
    setEntriesPage,
    entriesPageSize,
    setEntriesPageSize,
    exportError,
    setExportError,
    filterCostCodesData,
    filterCustomersData,
    filterEmployeesData,
    entriesData,
    entriesLoading,
    entriesCountData,
    correctionStats,
    correctionStatsLoading,
    filteredEntries,
    chartData,
    handleExport,
    canEditPunches,
    managedEditUserIds,
    isAdmin,
  };
}

export function AdminViewModelProvider({ children }: { children: React.ReactNode }) {
  const value = useAdminViewModelValue();
  return <AdminViewModelContext.Provider value={value}>{children}</AdminViewModelContext.Provider>;
}

export function useAdminViewModel(): AdminViewModelValue {
  const ctx = React.useContext(AdminViewModelContext);
  if (!ctx) throw new Error("useAdminViewModel must be used within AdminViewModelProvider");
  return ctx;
}
